import { useEffect, useRef, useState } from "react";

import { getReadingLayout } from "@utils/api-communication/RestApiCli";
import type { ReadingLayoutManifest } from "@components/common/ReadingLayoutRenderer";

const RETRY_INTERVAL_MS = 1500;

export type UseReadingLayoutPagesArgs = {
  hash?: string;
  textId?: string;
  enabled?: boolean;
  refetchSignal?: unknown;
};

export type UseReadingLayoutPagesResult = {
  manifest: ReadingLayoutManifest | null;
  pages: ReadingLayoutManifest["pages"] | null;
  error: unknown;
  isLoading: boolean;
};

export function useReadingLayoutPages({
  hash,
  textId,
  enabled = true,
  refetchSignal,
}: UseReadingLayoutPagesArgs): UseReadingLayoutPagesResult {
  const [manifest, setManifest] = useState<ReadingLayoutManifest | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const lastSignal = useRef(refetchSignal);
  const lastAttemptAt = useRef(0);
  const pageCount = manifest?.pages?.length ?? 0;

  useEffect(() => {
    if (!enabled) return;
    if (lastSignal.current === refetchSignal) return;
    lastSignal.current = refetchSignal;
    if (pageCount > 0 || isLoading) return;
    const now = Date.now();
    if (now - lastAttemptAt.current < RETRY_INTERVAL_MS) return;
    lastAttemptAt.current = now;
    setAttempt((n) => n + 1);
  }, [refetchSignal, enabled, pageCount, isLoading]);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (!hash && !textId) {
      setManifest(null);
      setError(new Error("Missing hash/textId."));
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        setIsLoading(true);
        setError(null);

        const nextManifest = (await getReadingLayout({ hash, textId })) as
          | ReadingLayoutManifest
          | null
          | undefined;
        const safeManifest: ReadingLayoutManifest = {
          layout: nextManifest?.layout ?? {
            fontSize: 0,
            wordSpacing: 0,
            lineHeight: 0,
            maxLineWidth: 1,
            aoiPadding: 0,
            textWindowHeight: 1,
          },
          pages: Array.isArray(nextManifest?.pages) ? nextManifest.pages : [],
          textOrientation: nextManifest?.textOrientation,
        };

        if (!cancelled) {
          setManifest(safeManifest);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setManifest(null);
          setError(err);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hash, textId, enabled, attempt]);

  return { manifest, pages: manifest?.pages ?? null, error, isLoading };
}
